import { createSlice } from "@reduxjs/toolkit";
import {
    commonSubscriptionPrice,
    freeSubscriptionPrice,
    InitialSubscriptions,
} from "../domain/models/Subscription";
import { SubscriptionServiceImpl } from "../services/SubscriptionServiceImpl";
import { handleCollectionThunk } from "../services/ApiServiceImpl";
import { AppState } from "../store";
import { SubscriptionStatus } from "../domain/enums/SubscriptionStatusEnum";
import { differenceInDays } from "date-fns";
import _ from "lodash";
import { SubscriptionDTO } from "../domain/models/dto/SubscriptionDTO";
import currencyServiceFactory from "../services/CurrencyServiceImpl";
import { Currency } from "../domain/enums/CurrencyEnum";
import { EasyPayNewOrderDTO } from "../integrations/easypay/interfaces/EasyPayDTO";

export const subscriptionsSlice = createSlice({
    name: "subscriptions",
    initialState: InitialSubscriptions,
    reducers: {},
    extraReducers: (builder) => {
        // handle get /subscription
        handleCollectionThunk(
            builder,
            SubscriptionServiceImpl.getAllSubscriptionsThunk
        );

        // handle post /subscription
        builder.addCase(
            SubscriptionServiceImpl.createSubscriptionThunk.fulfilled,
            (state, action) => {
                if (_.get(action.payload as EasyPayNewOrderDTO, "forwardUrl")) {
                    return;
                }
                state.rows.push(action.payload as SubscriptionDTO);
            }
        );

        // handle patch /subscription/auto-activation
        builder.addCase(
            SubscriptionServiceImpl.changeSubscriptionAutoActivationThunk
                .fulfilled,
            (state, action) => {
                if (action.payload.status) {
                    const active = getActiveSubscription(state.rows);
                    active.autoActivation = !active.autoActivation;
                }
            }
        );

        // handle get /subscription/settings?type=
        builder.addCase(
            SubscriptionServiceImpl.getSubscriptionSettingsThunk.fulfilled,
            (state, action) => {
                state.settings = action.payload;
            }
        );
    },
});

const getTimestamp = (value: string | null | undefined) => {
    if (!value) return Number.NEGATIVE_INFINITY;
    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
};

export const getActiveSubscription = (subscriptions: SubscriptionDTO[]) => {
    return subscriptions
        .filter((item) => item.status === SubscriptionStatus.ACTIVE)
        .map((item, index) => ({ item, index }))
        .sort((a, b) => {
            const activationDifference = compareTimestamps(
                b.item.activationTime,
                a.item.activationTime
            );
            if (activationDifference !== 0) return activationDifference;

            const updatedDifference = compareTimestamps(b.item.updatedAt, a.item.updatedAt);
            if (updatedDifference !== 0) return updatedDifference;

            return a.index - b.index;
        })
        .at(0)?.item;
};

const compareTimestamps = (left: string | null | undefined, right: string | null | undefined) => {
    const leftTimestamp = getTimestamp(left);
    const rightTimestamp = getTimestamp(right);
    if (leftTimestamp === rightTimestamp) return 0;
    return leftTimestamp > rightTimestamp ? 1 : -1;
};

export const selectRawSubscriptions = (state: AppState) =>
    state.subscriptions.rows;

export const selectIsTrialSubscriptionAvailable = (state: AppState) =>
    !_.get(state, "subscriptions.rows.length");

export const selectActiveSubscription = (state: AppState) =>
    getActiveSubscription(selectRawSubscriptions(state));

export const selectCommonSubscriptionPrice = (state: AppState) => {
    return {
        ...commonSubscriptionPrice,
        amount: state.subscriptions.settings?.priceAmount,
    };
};

export const selectPriceDollars = (state: AppState) => {
    const active = selectActiveSubscription(state);
    return active
        ? _.get(active, "price")
        : selectIsTrialSubscriptionAvailable(state)
        ? freeSubscriptionPrice
        : selectCommonSubscriptionPrice(state);
};

export const selectPriceHryvnia = (state: AppState) => {
    const currencyService = currencyServiceFactory();
    const conversion = currencyService.convertCurrency(
        selectPriceDollars(state),
        Currency.UAH
    );
    return conversion.available ? conversion.price : undefined;
};

export const selectActivationTime = (state: AppState) => {
    const active = selectActiveSubscription(state);
    return _.get(active, "activationTime");
};

export const selectDeactivationTime = (state: AppState) => {
    const active = selectActiveSubscription(state);
    return _.get(active, "deactivationTime");
};

export const selectTrialSubscriptionDurationDays = (state: AppState) =>
    state.subscriptions.settings?.trialDurationDays;

export const selectCommonSubscriptionDurationDays = (state: AppState) =>
    state.subscriptions.settings?.durationDays;

export const selectSubscriptionDurationDays = (state: AppState) => {
    if (selectIsTrialSubscriptionAvailable(state))
        return selectTrialSubscriptionDurationDays(state);
    const activeSubscription = selectActiveSubscription(state);
    if (!activeSubscription) return selectCommonSubscriptionDurationDays(state);
    const activationTime = getTimestamp(activeSubscription.activationTime);
    const deactivationTime = getTimestamp(activeSubscription.deactivationTime);
    if (!Number.isFinite(activationTime) || !Number.isFinite(deactivationTime)) {
        return selectCommonSubscriptionDurationDays(state);
    }
    return differenceInDays(new Date(deactivationTime), new Date(activationTime));
};

export default subscriptionsSlice.reducer;
